// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { getFilesFromDirectory } from './files';
import i18n from '../i18n';
import { getSketchFromSessionStorage } from './sketch';
import { pickSketchFile } from './sketchFile';
import type { FormImageSource } from '../offline/formStorage';
import {
  generateImageNameWithROID,
  type GenerateImageNameParams,
  type OrderMetadata,
} from './imageNaming';
import { uploadToHetzner, type EntityType } from './storage';

// Types
import { Sketch } from '../components/forms/AddBasicInfoAccidentForm/types';
import { OrderType } from '../types/orders';

export type ImageData = {
  creatingDate: string;
  /**
   * Set when the upload went through Hetzner Object Storage (current path
   * after 2026-05 storage migration). Backend stores storageProvider="hetzner".
   */
  storageKey?: string;
  /**
   * @deprecated Set only for legacy uploads (rare, transient migration window).
   * Backend stores storageProvider="cloudinary-legacy".
   */
  cloudinaryPublicId?: string;
  name: string;
  // T56 fix: Include sketch data for canvas images
  sketchSvg?: string | null;
  sketchFabricJson?: string | null;
};

/** @deprecated alias kept for internal callers; use ImageData */
type imageData = ImageData;

/**
 * Input shape for the `createImage` mutation. Exactly one of
 * `storageKey` (Hetzner, new) or `cloudinaryPublicId` (legacy) is set —
 * `buildCreateImageInput` enforces this invariant so a malformed input
 * (both/neither) can never reach the backend silently.
 */
export interface CreateImageInput {
  name: string;
  creatingDate: string;
  orderId: string;
  storageKey?: string;
  cloudinaryPublicId?: string;
  sketchSvg?: string | null;
  sketchFabricJson?: string | null;
  // Idempotencia offline (GAP 8): id estable de la op de la cola de imágenes.
  // Solo lo envía el sync de la cola offline; los createImage interactivos no.
  // Si el navegador se cierra entre createImage y removeImageUpload, el retry
  // llega con el MISMO clientOpId → el backend deduplica (no crea Image doble).
  clientOpId?: string;
}

/**
 * Builds a `createImage` mutation input from an uploaded `ImageData`,
 * asserting the dual-identifier XOR invariant (mirrors the backend
 * `ImageService.create` validation). Throws loudly instead of letting a
 * broken Image row (neither storageKey nor cloudinaryPublicId) be created.
 */
export const buildCreateImageInput = (
  image: ImageData,
  orderId: string,
): CreateImageInput => {
  const hasKey = !!image.storageKey;
  const hasLegacy = !!image.cloudinaryPublicId;
  if (hasKey === hasLegacy) {
    throw new Error(
      `buildCreateImageInput: exactly one of storageKey / cloudinaryPublicId required (name=${image.name})`,
    );
  }
  return {
    name: image.name,
    creatingDate: image.creatingDate,
    orderId,
    ...(hasKey
      ? { storageKey: image.storageKey }
      : { cloudinaryPublicId: image.cloudinaryPublicId }),
    sketchSvg: image.sketchSvg ?? null,
    sketchFabricJson: image.sketchFabricJson ?? null,
  };
};

type ImageType = 'photo' | 'canvas';

/**
 * Error lanzado por `UploadImage` cuando `options.throwOnError === true` y
 * la subida a Cloudinary falla (network, 4xx, 5xx, JSON inválido). Permite
 * al caller distinguir entre "Cloudinary unreachable" (encolar para reintento
 * offline) y otros errores de la mutation Apollo posterior.
 *
 * `cloudinaryStatus` es undefined cuando el fallo es de red (no llegó respuesta).
 */
export class CloudinaryUploadError extends Error {
  public readonly cloudinaryStatus?: number;
  constructor(message?: string, cloudinaryStatus?: number) {
    super(message ?? 'Cloudinary upload failed');
    this.name = 'CloudinaryUploadError';
    this.cloudinaryStatus = cloudinaryStatus;
  }
}

// El nombrado ROID vive en `utils/imageNaming.ts` — módulo PURO, sin el
// cliente Apollo detrás, para que `imagePlan.ts` pueda usarlo sin arrastrar
// media aplicación. Se reexporta para no tocar los imports existentes.
export { generateImageNameWithROID };
export type { GenerateImageNameParams };

export type { OrderMetadata };

export interface UploadImageOptions {
  /**
   * Si `true`, los fallos del fetch a Cloudinary lanzan `CloudinaryUploadError`
   * en lugar de loggear silenciosamente. Permite a los callers migrados a la
   * cola offline detectar el fallo y diferir la subida.
   *
   * Default `false` (retrocompatible con flujos no migrados).
   */
  throwOnError?: boolean;

  /**
   * Tenant-scoped Hetzner presigned PUT requires both. **REQUIRED** desde
   * 2026-05 (cleanup post-migración Cloudinary→Hetzner): UploadImage lanza
   * un Error si falta cualquiera de los dos. El fallback legacy directo a
   * Cloudinary se eliminó para evitar regresiones que vuelvan a saturar
   * Cloudinary storage.
   */
  orderId: string;
  entityType: EntityType;

  /**
   * De dónde leer las fotos y el fichero del croquis, y los datos del croquis.
   * Por defecto, las carpetas OPFS y `sessionStorage` de la pantalla; el
   * reintento de la cola pasa los suyos en memoria para no leer el formulario
   * abierto (spec outbox-autocontenido v2 § 5.1).
   */
  formImages?: Pick<FormImageSource, 'list'>;
  formStore?: Pick<Storage, 'getItem'>;

  /**
   * Nombre del fichero del croquis dentro de `canvasDirectoryName`: se sube
   * ESE y no «el primero de la carpeta» (ver `pickSketchFile`). La carpeta la
   * comparten las pestañas y los asistentes de crear y editar, así que el
   * primero podía ser el croquis de otro borrador. Sin él, el primero
   * (borradores de antes de apuntar el nombre, y los guardados que aún no lo
   * pasan).
   */
  canvasFileName?: string | null;
}

/**
 * Sube croquis y (opcionalmente) fotos, y devuelve los datos para crear los
 * registros `Image`.
 *
 * `picturesDirectoryName` es opcional desde el outbox de fotos (2026-08): los
 * `saveData` encolan las fotos con `enqueuePhotosForUpload` y llaman aquí SOLO
 * para el croquis, que sigue siendo bloqueante — es uno solo, pesa poco y su
 * ruta de rescate offline ya está probada. Con el parámetro a `undefined` el
 * bloque de fotos es un no-op.
 */
export const UploadImage = async (
  picturesDirectoryName: string | undefined,
  canvasDirectoryName?: string,
  orderMetadata?: OrderMetadata,
  options?: UploadImageOptions
) => {
  const arrayImages: imageData[] = [];
  const throwOnError = options?.throwOnError ?? false;
  // Hetzner es obligatorio: el path legacy directo a Cloudinary se eliminó
  // (cleanup post-migración 2026-05; impedir regresiones que saturen storage).
  if (!options?.orderId || !options?.entityType) {
    throw new Error(
      'UploadImage requires options.orderId and options.entityType (Hetzner is mandatory)',
    );
  }
  const orderId = options.orderId;
  const entityType = options.entityType;

  const formImages = options.formImages;
  const listFiles = formImages
    ? (dir: string) => formImages.list(dir)
    : getFilesFromDirectory;
  const files = picturesDirectoryName
    ? (await listFiles(picturesDirectoryName)).map(
        (item) => item.file
      )
    : [];

  const imagesBlob = files.map((file) => new Blob([file], { type: file.type }));

  if (canvasDirectoryName) {
    const fileCanvas = pickSketchFile(
      await listFiles(canvasDirectoryName),
      options.canvasFileName
    );
    if (!fileCanvas && options.canvasFileName) {
      // El croquis del borrador ya no está (otra pestaña vació la carpeta al
      // dibujar el suyo): mejor sin croquis que con el de otro. Pero que se
      // SEPA: un croquis perdido en silencio no lo ve nadie (revisión de la
      // Task 9). Aviso, no error: el guardado sigue con lo demás.
      console.warn(
        `[imágenes] el croquis ${options.canvasFileName} no está en ${canvasDirectoryName}; no se sube ninguno`
      );
      Sentry.captureMessage(
        `Croquis del borrador ausente en ${canvasDirectoryName}: no se sube ninguno`,
        {
          level: 'warning',
          tags: { guard: `sketch-missing:${entityType}` },
          extra: { canvasFileName: options.canvasFileName, orderId },
        }
      );
    }
    if (fileCanvas) {
      const blobCanvas = new Blob([fileCanvas.file], {
        type: fileCanvas.file.type,
      });

      // Generate name with ROID pattern if metadata provided, otherwise use legacy format
      const nameCanvas = orderMetadata
        ? generateImageNameWithROID({
            ...orderMetadata,
            imageType: 'canvas',
          })
        : fileCanvas.file.name + '_canvas';

      try {
        const sketchType =
          orderMetadata?.orderType === OrderType.Action ? 'action' : 'accident';
        const sketchData = getSketchFromSessionStorage(sketchType, options.formStore);
        const contentType = fileCanvas.file.type || 'image/jpeg';

        const { storageKey } = await uploadToHetzner(blobCanvas, {
          orderId,
          entityType,
          roidName: nameCanvas,
          contentType,
        });
        arrayImages.push({
          creatingDate: new Date().toISOString(),
          storageKey,
          name: nameCanvas,
          sketchSvg: sketchData.svgContent,
          sketchFabricJson: sketchData.fabricJson,
        });
      } catch (err) {
        Sentry.captureException(err);
        console.log('[imágenes] error subiendo el croquis', err);
        if (throwOnError) {
          if (err instanceof CloudinaryUploadError) throw err;
          // Network error (Failed to fetch, ERR_INTERNET_DISCONNECTED…) or HetznerUploadError
          // Con la causa: así la cola sabe si fue transporte (sin servidor,
          // plazo vencido) y no gasta intento (spec 2026-10-09 § 9).
          throw Object.assign(
            new CloudinaryUploadError(
              (err as Error)?.message ?? 'Sketch upload network error',
              undefined,
            ),
            { cause: err },
          );
        }
      }
    }
  }

  try {
    await Promise.all(
      imagesBlob.map(async (file, index) => {
        const blob = file;
        // Generate name with ROID pattern if metadata provided, otherwise use original filename
        let imageName = files[index].name;
        if (orderMetadata) {
          imageName = generateImageNameWithROID({
            ...orderMetadata,
            imageType: 'photo',
            sequence: index + 1,
          });
        }
        const contentType = files[index].type || 'image/jpeg';

        const { storageKey } = await uploadToHetzner(blob, {
          orderId,
          entityType,
          roidName: imageName,
          contentType,
        });
        arrayImages.push({
          creatingDate: new Date().toISOString(),
          storageKey,
          name: imageName,
        });
      })
    );
  } catch (err) {
    Sentry.captureException(err);
    console.log('[imágenes] error subiendo las imágenes', err);
    if (throwOnError) {
      if (err instanceof CloudinaryUploadError) throw err;
      throw new CloudinaryUploadError(
        (err as Error)?.message ?? 'Photo upload network error',
        undefined,
      );
    }
  }

  return arrayImages;
};

/**
 * C-11.13: comparación robusta de sketches por identidad de registro BD.
 *
 * Antes esta función comparaba sólo `name`. Eso tiene 2 problemas latentes:
 *
 * 1. **Falsos negativos al repintar si alguien preserva `name`**: hoy
 *    `handleAddSketch` no pone `name` en el newSketch (el `name` lo asigna
 *    `UploadImage` con `generateImageNameWithROID` justo antes de subir a
 *    Cloudinary). Si en el futuro alguien añadiera `name` a `newSketch`
 *    (mejora razonable de UX), el comparador por `name` dejaría de detectar
 *    el repaint silenciosamente → el sketch viejo se mantendría en BD.
 *
 * 2. **`name` es un valor descriptivo, no un identificador**. La identidad
 *    semántica de un sketch es su registro BD (`databaseId`) y su payload
 *    (`id`: data URI cuando se está editando localmente, ObjectId BD cuando
 *    se cargó del server). Comparar por estos campos es más explícito.
 *
 * **Reglas** (orden de evaluación):
 *
 * | Caso             | original | modified                                      | Resultado     |
 * |------------------|----------|-----------------------------------------------|---------------|
 * | Sin sketch       | null     | null                                          | nada          |
 * | Nuevo            | null     | obj                                           | save          |
 * | Borrado          | obj      | null                                          | remove        |
 * | Sin cambio       | obj      | obj con mismo `databaseId` y mismo `id`       | nada          |
 * | Re-pintado       | obj      | obj con mismo `databaseId`, distinto `id`     | save + remove |
 * | Reemplazo total  | obj      | obj con `databaseId` distinto                 | save + remove |
 *
 * Equivalencia con la lógica anterior por `name`:
 * - Caso "sin cambio": ambas devuelven null/null (en el flujo normal,
 *   modified.name === original.name === "At_xxx_canvas").
 * - Caso "re-pintado": la lógica vieja detectaba `undefined !== name` y la
 *   nueva detecta `dataURI !== BD_ID`. Mismo resultado.
 *
 * @see ActionEdition handleAddSketch
 * @see ActionsValidation/Animals/Action/saveData.ts (lectura de originalSketch)
 */
export function compareSketchJson(
  modifiedSketchJson: string | null,
  originalSketchJson: string | null,
) {
  let sketchToSaveOrUpdate: Sketch | null = null;
  let sketchToRemove: Sketch | null = null;

  if (!modifiedSketchJson && !originalSketchJson) {
    return { sketchToSaveOrUpdate, sketchToRemove };
  }

  if (modifiedSketchJson && !originalSketchJson) {
    sketchToSaveOrUpdate = JSON.parse(modifiedSketchJson);
    return { sketchToSaveOrUpdate, sketchToRemove };
  }

  if (!modifiedSketchJson && originalSketchJson) {
    sketchToRemove = JSON.parse(originalSketchJson);
    return { sketchToSaveOrUpdate, sketchToRemove };
  }

  // Both sides present — comparar por identidad de registro BD + payload.
  const modified = JSON.parse(modifiedSketchJson as string);
  const original = JSON.parse(originalSketchJson as string);

  const sameDbRecord =
    modified?.databaseId &&
    original?.databaseId &&
    modified.databaseId === original.databaseId;
  const samePayload =
    modified?.id && original?.id && modified.id === original.id;

  if (sameDbRecord && samePayload) {
    // Sin cambio real — el shape JSON difiere por reformateo de date / road
    // o por orden de keys, pero ambos referencian el mismo blob en BD.
    return { sketchToSaveOrUpdate, sketchToRemove };
  }

  // Repintado del mismo registro BD (databaseId conservado, id —el data URI—
  // distinto) o reemplazo total (databaseId distinto). En ambos casos
  // borramos el viejo de BD y subimos el nuevo a Cloudinary + creamos record.
  sketchToSaveOrUpdate = modified;
  sketchToRemove = original;
  return { sketchToSaveOrUpdate, sketchToRemove };
}

// Function to compare two JSONs and generate arrays of images to save and remove
export function compareImageJSONs(
  originalJson?: string,
  modifiedJson?: string
): { imagesToSave: any[]; imagesToRemove: any[] } {
  // Arrays to store images to add and remove

  const imagesToSave: any[] = [];

  const imagesToRemove: any[] = [];

  if (originalJson && modifiedJson) {
    // Parse JSON strings into objects
    const originalImages = JSON.parse(originalJson);
    const modifiedImages = JSON.parse(modifiedJson);

    // Convert arrays of objects to sets of image names for easy comparison
    const originalNamesSet = new Set(
      originalImages.map((image: any) => image.name)
    );
    const modifiedNamesSet = new Set(
      modifiedImages.map((image: any) => image.name)
    );

    // Determine images to save (present in modified JSON but not in the original)

    modifiedImages.forEach((image: any) => {
      if (!originalNamesSet.has(image.name)) {
        imagesToSave.push(image);
      }
    });

    // Determine images to remove (present in original JSON but not in the modified)

    originalImages.forEach((image: any) => {
      if (!modifiedNamesSet.has(image.name)) {
        imagesToRemove.push(image);
      }
    });
  }

  return {
    imagesToSave,
    imagesToRemove,
  };
}
